<div class="mt-8 mb-4">

    <div class="flex flex-row justify-between w-full mb-4">
        <app-btn-back></app-btn-back>
    </div>

    <div class="mb-4 border-0 w-full">

        <h1 class="font-semibold text-2xl text-blueGray-700">
            {{ report()?.quizTitle ?? 'Quiz Report' }}
        </h1>
        <p class="text-sm text-gray-400">Completed on {{ report()?.completedAt?.toDate() | date:'medium' }}</p>

        @if (isLoading()) {
        <div class="flex flex-col items-center justify-center h-64">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p class="mt-4 text-gray-600">Generating your detailed report...</p>
        </div>
        } @else if (report()) {

        <div class="max-w-6xl mx-auto min-h-screen mt-4">
            @if (isLoading()) {
            <div class="flex justify-center items-center h-96">
                <div class="animate-bounce text-xl font-bold text-indigo-600">Loading Analytics...</div>
            </div>
            } @else {
            @if (report()) {
            <header class="mb-4">
                <p class="text-gray-500 text-md italic">Global Average: {{ report().summary.averageScore |
                    number:'1.0-0'
                    }}%</p>
            </header>

            <div class="grid grid-cols-4 grid-rows-1 w-full gap-4 mb-4">

                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div class="flex items-center gap-3 mb-2">
                        <colored-icon iconClass="fas fa-children" bgColorClass="bg-indigo-100"
                            textColorClass="text-indigo-600"></colored-icon>
                        <span class="text-sm font-medium text-primary-300 tracking-wider">Students</span>
                    </div>
                    <div class="text-2xl font-bold text-slate-900">{{ report().summary.participantCount }}</div>
                    <div class="text-xs text-blueGray-400 mt-1">Joined the session</div>
                </div>

                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div class="flex items-center gap-3 mb-2">
                        <colored-icon iconClass="fas fa-circle-question" bgColorClass="bg-emerald-100"
                            textColorClass="text-emerald-600"></colored-icon>
                        <span class="text-sm font-medium text-primary-300 tracking-wider">Questions</span>
                    </div>
                    <div class="text-2xl font-bold text-slate-900">{{ report().summary?.totalQuestions ?? '-' }}</div>
                    <div class="text-xs text-blueGray-400 mt-1">Total in this quiz</div>
                </div>

                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div class="flex items-center gap-3 mb-2">
                        <colored-icon iconClass="fas fa-chart-bar" bgColorClass="bg-amber-100"
                            textColorClass="text-amber-600"></colored-icon>
                        <span class="text-sm font-medium text-primary-300 tracking-wider">Class Avg</span>
                    </div>
                    <div class="text-2xl font-bold text-slate-900">{{ report().summary.averageScore | number:'1.0-0' }}%
                    </div>
                    <div class="text-xs text-blueGray-400 mt-1">Overall performance</div>
                </div>

                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div class="flex items-center gap-3 mb-2">
                        <colored-icon iconClass="fas fa-clock" bgColorClass="bg-rose-100"
                            textColorClass="text-rose-600"></colored-icon>
                        <span class="text-sm font-medium text-primary-300 tracking-wider">Total Time</span>
                    </div>
                    <div class="text-2xl font-bold text-slate-900">{{ report().summary?.quizDuration ?? '-' }} min
                    </div>
                    <div class="text-xs text-blueGray-400 mt-1">Cumulative student effort</div>
                </div>
            </div>

            <div class="grid grid-cols-1 grid-rows-2 md:grid-cols-2 md:grid-rows-1 gap-4">

                <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-200 w-full max-w-full overflow-hidden">
                    <h2 class="text-sm text-blueGray-400 mb-4">Mastery by Bible Book</h2>
                    <div class="h-64 w-full relative">
                        @if (bookMasteryData()) {
                        <canvas baseChart [data]="bookMasteryData()!" [options]="barOptions" [type]="'bar'">
                        </canvas>
                        }
                    </div>
                </div>

                <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-200 w-full max-w-full overflow-hidden">
                    <h2 class="text-sm text-blueGray-400 mb-4">Efficiency (Time vs. Score)</h2>
                    <div class="h-64 w-full relative">
                        @if (scatterData()) {
                        <canvas baseChart [data]="scatterData()!" [options]="scatterOptions" [type]="'scatter'">
                        </canvas>
                        }
                    </div>
                </div>

            </div>

            <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mt-4 w-full max-w-full overflow-hidden">
                <div class="flex items-center gap-3 mb-3">
                    <colored-icon iconClass="fas fa-bullseye" bgColorClass="bg-rose-100" textColorClass="text-rose-600"></colored-icon>
                    <span class="text-sm font-semibold text-slate-800">Question Difficulty vs. Bible Reference</span>
                </div>
                <div class="h-64 w-full relative">
                    @if (difficultyBubbleData()) {
                    <canvas baseChart [data]="difficultyBubbleData()!" [options]="bubbleOptions" [type]="'bubble'">
                    </canvas>
                    } @else {
                    <div class="flex items-center justify-center w-full h-full">
                        <span class="text-xs text-gray-400 italic">No question metrics data found.</span>
                    </div>
                    }
                </div>
            </div>

            <section class="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h2 class="text-xm text-blueGray-400">Toughest Questions (Class Killers)</h2>
                </div>
                <div class="divide-y divide-gray-100">
                    @for (q of report().troubleQuestions; track q.questionId) {
                    <div class="p-4 flex items-start justify-between gap-4">
                        <div class="flex-1">
                            <span class="text-xs font-bold text-indigo-600 uppercase">{{ q.book }} {{ q.chapter }}</span>
                            <p class="text-sm font-semibold text-slate-800 mt-1">{{ q.questionText || 'Question' }}</p>
                            <p class="text-[10px] text-gray-400 mt-0.5">Question ID: {{ q.questionId | slice:0:8 }}...</p>
                        </div>
                        <div class="text-right whitespace-nowrap">
                            <div class="text-sm font-bold text-red-500">{{ q.wrongCount }} <span class="text-[10px] font-normal text-gray-400">wrong</span></div>
                            @if (q.unansweredCount > 0) {
                            <div class="text-sm font-bold text-amber-500 mt-0.5">{{ q.unansweredCount }} <span class="text-[10px] font-normal text-gray-400">skipped</span></div>
                            }
                        </div>
                    </div>
                    }
                </div>
            </section>

            <section class="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h2 class="text-xm text-blueGray-400">Student Performance</h2>
                </div>
                <div class="divide-y divide-gray-100">
                    @for (item of report().rankings; track $index) {
                    <div class="p-4 flex justify-between">
                        <div class="flex flex-row items-center">
                            <span class="font-bold text-gray-400">#{{ $index + 1 }}</span>
                            <span class="font-semibold text-primary-500 titlecase ml-2">{{ item.name }}</span>
                        </div>
                        <div class="text-right flex flex-row items-end">
                            <span class="font-bold text-primary-500">{{ item.userScore ?? item.totalScore }}</span>
                            <span class="text-gray-400 inline ml-1">points</span>
                        </div>
                    </div>
                    }
                </div>
            </section>

            <section class="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-full max-w-full">
                <div class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                    <colored-icon iconClass="fas fa-table" bgColorClass="bg-emerald-100" textColorClass="text-emerald-600"></colored-icon>
                    <h2 class="text-xm text-blueGray-400">Student Performance Matrix</h2>
                </div>
                <div class="p-4 w-full max-w-full overflow-hidden">
                    <p class="text-[11px] text-slate-400 m-0 mb-3">Response matrix showing correct (✓), incorrect (✗), or unanswered (-) answers per student per question.</p>
                    
                    @if (report().matrix && report().matrix.students && report().matrix.students.length > 0) {
                        <div class="overflow-x-auto w-full max-w-full max-h-[500px]">
                            <table class="w-full text-left border-collapse min-w-full">
                                <thead>
                                    <tr class="border-b border-slate-100 bg-slate-50">
                                        <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider sticky top-0 left-0 bg-slate-50 z-30 shadow-[2px_2px_5px_-2px_rgba(0,0,0,0.1)] w-64 min-w-[180px] sm:min-w-[280px]">Question Reference & Text</th>
                                        @for (student of report().matrix.students; track student.userId) {
                                            <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center min-w-[80px] sm:min-w-[100px] md:min-w-[120px] sticky top-0 bg-slate-50 z-20 border-b border-slate-100">
                                                <div class="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-center">
                                                    @if (getStudentPhotoUrl(student.userId)) {
                                                        <img [src]="getStudentPhotoUrl(student.userId)" alt="" class="w-5 h-5 rounded-full border border-slate-100">
                                                    } @else {
                                                        <div class="w-5 h-5 rounded-full bg-slate-100 border border-slate-200 text-slate-500 flex items-center justify-center font-bold text-[9px] uppercase">
                                                            {{ (student.userName || '').substring(0, 2) }}
                                                        </div>
                                                    }
                                                    <span class="break-words max-w-[80px] sm:max-w-none">{{ student.userName | titlecase }}</span>
                                                </div>
                                            </th>
                                        }
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100">
                                    @for (q of report().matrix.questions; track q.questionId; let i = $index) {
                                        @let qMeta = report().questionStats[i];
                                        <tr class="hover:bg-slate-50/50 transition-colors">
                                            <td class="py-2.5 px-3 sticky left-0 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10">
                                                <div class="flex flex-col">
                                                    <span class="text-[10px] font-bold text-indigo-600 uppercase">
                                                        Q{{ i + 1 }} @if (qMeta?.book) { • {{ qMeta.book }} {{ qMeta.chapter }}:{{ qMeta.verse }} }
                                                    </span>
                                                    <span class="text-xs text-slate-700 font-medium line-clamp-2 mt-0.5" [title]="q.questionText">
                                                        {{ q.questionText }}
                                                    </span>
                                                </div>
                                            </td>
                                            @for (student of report().matrix.students; track student.userId) {
                                                @let cell = report().matrix.grid[q.questionId]?.[student.userId];
                                                <td class="py-2.5 px-3 text-center">
                                                    @if (cell?.status === 'correct') {
                                                        <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 font-bold text-xs" title="Correct">
                                                            <i class="fas fa-check"></i>
                                                        </span>
                                                    } @else if (cell?.status === 'wrong') {
                                                        <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-50 text-rose-600 font-bold text-xs" title="Incorrect">
                                                            <i class="fas fa-times"></i>
                                                        </span>
                                                    } @else {
                                                        <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-50 text-slate-400 font-semibold text-xs" title="Unanswered / Skipped">
                                                            -
                                                        </span>
                                                    }
                                                </td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    } @else {
                        <div class="text-xs text-slate-400 italic py-8 text-center bg-slate-50 rounded-xl">
                            No participant data available to display matrix.
                        </div>
                    }
                </div>
            </section>

            } @else {
            <div class="flex flex-col items-center justify-center h-64">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                <p class="mt-4 text-gray-500">Compiling Report...</p>
            </div>
            }
            }
        </div>

        } @else {
        <div class="text-center py-20">
            <p class="text-gray-500">Report not found. It may still be processing.</p>
            <button routerLink="/" class="mt-4 text-primary-600 font-bold">Return Home</button>
        </div>
        }
    </div>